/* eslint-disable no-tabs */
/* eslint-disable react/jsx-indent */
import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/react-hooks';
import ActiveStudent from '../ManualGradingReviewComponents/ActiveStudent/index';
import queryGetGrades from './queryGetGrades.gql';
import { transformResponse } from './mapQueryResponse';
import ManualGradingFormContainer from '../ManualGradingFormContainer';
import ManualGradingPluginContainer from '../ManualGradingPluginContainer';
import Breadcrumbs from './Breadcrumbs';
import { INCOMPLETE_ATTEMPT, NOT_ATTEMPTED } from '../scoreReason';

import {
  Content,
  NavBar,
  Body,
  FullScreen,
  Sidebar,
  NotYetAttemptedWrapper,
} from '../ManualGradingReviewComponents/Layout';

import messages from './messages';

/**
 * A wrapper that exist only to elevate the plugin component tree away
 * from rerendering due to query updates.  Ideally, we would remove the
 * queries from this container, and place them closer to the containers
 * that actually need them.  This is a quicker bandaid solution
 * */
const ManualGradingReviewContainerWrapper = (props) => {
  const pluginContainer =
    (<ManualGradingPluginContainer
      cohortId={props.cohortId}
      deploymentId={props.deploymentId}
      interactiveId={props.interactiveId}
      componentId={props.componentId}
      studentId={props.studentId}
    />);

  return (
    <ManualGradingReviewContainer {...props} pluginContainer={pluginContainer} />
  );
};

ManualGradingReviewContainerWrapper.propTypes = {
  cohortId: PropTypes.string,
  deploymentId: PropTypes.string,
  componentId: PropTypes.string,
  studentId: PropTypes.string,
  interactiveId: PropTypes.string,
};

/**
 * Our main container for the review screen.
 */
const ManualGradingReviewContainer = ({ cohortId, deploymentId, componentId, interactiveId, studentId, pluginContainer }) => {
  const variables = { cohortId, deploymentId, componentId, interactiveId };
  const [childProps, setChildProps] = useState(null);

  const { data, loading } = useQuery(queryGetGrades, {
    variables,
  });

  // extra data transformation for child components.
  useEffect(() => {
    if (!loading) {
      const { students, components, ancestry } = transformResponse(data);
      const component = components[componentId];
      const maxScore = component.maxScore;
      const activeStudent = students[studentId];
      const grades = activeStudent.grades;

      const notReadyForGrading = [INCOMPLETE_ATTEMPT, NOT_ATTEMPTED].includes(activeStudent.state);
      const lastGrade = grades.length < 1 ? {} : grades.sort((a, b) => +b.createdAt - +a.createdAt)[0];

      setChildProps({
        ...lastGrade,
        students,
        component,
        activeStudent,
        ancestry,
        studentId,
        attemptId: activeStudent.attemptId,
        maxScore,
        notReadyForGrading,
      });
    }

    return () => {};
  }, [componentId, data, loading, studentId]);

  return (
    <Fragment>
      <FullScreen>
        <NavBar>
          {childProps && <Breadcrumbs
            {...childProps.component}
            cohortId={cohortId}
            deploymentId={deploymentId}
            studentId={studentId}
            ancestry={childProps.ancestry}
          />}
          {childProps && <ActiveStudent
            variables={{ ...variables }}
            students={childProps.students}
            activeStudent={childProps.activeStudent}
            maxScore={childProps.maxScore}
            attemptId={childProps.attemptId}
          />}
        </NavBar>
        <Body key={[cohortId, deploymentId, interactiveId, studentId].join('-')}>
          {!loading && childProps && childProps.notReadyForGrading ? (
            <NotYetAttemptedWrapper>
              <h2>
                <FormattedMessage {...messages.noAttempt} />
              </h2>
            </NotYetAttemptedWrapper>
          ) : (
            <Fragment>
              <Content>
                {pluginContainer}
              </Content>
              <Sidebar>
                {loading || !childProps ? <div></div> :
                  <ManualGradingFormContainer
                    {...childProps}
                    deploymentId={deploymentId}
                    componentId={componentId}
                    cohortId={cohortId}
                    interactiveId={interactiveId}
                  />}
              </Sidebar>
            </Fragment>
          )}
        </Body>
      </FullScreen>
    </Fragment>
  );
};

ManualGradingReviewContainer.propTypes = {
  cohortId: PropTypes.string,
  deploymentId: PropTypes.string,
  componentId: PropTypes.string,
  studentId: PropTypes.string,
  interactiveId: PropTypes.string,
  pluginContainer: PropTypes.element,
};

export default ManualGradingReviewContainerWrapper;
